import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl } from './config';

const ReadingAssignmentViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);
  const saveIntervalRef = useRef(null);

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [existingRecording, setExistingRecording] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioPlayerRef = useRef(null);

  // Speech recognition / reading tracking states
  const [isListening, setIsListening] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [interimWordIndex, setInterimWordIndex] = useState(-1); // for real-time highlighting
  const [wordStatuses, setWordStatuses] = useState([]); // 'unread' | 'correct' | 'mistake' | 'reading'
  const [mistakes, setMistakes] = useState([]); // {index, expected, spoken}
  const [wordsPerMinute, setWordsPerMinute] = useState(0);
  const [readingStartTime, setReadingStartTime] = useState(null);
  const [lastSpokenText, setLastSpokenText] = useState('');
  const [speechSupported, setSpeechSupported] = useState(true);
  const [savingAccuracy, setSavingAccuracy] = useState(false);
  const [existingAccuracy, setExistingAccuracy] = useState(null);
  const recognitionRef = useRef(null);
  const currentWordIndexRef = useRef(0);
  const interimWordIndexRef = useRef(-1);
  const phraseStartIdxRef = useRef(0); // where current speech phrase began in passage
  const wordStatusesRef = useRef([]);
  const mistakesRef = useRef([]);
  const passageWordsRef = useRef([]);
  const readingStartTimeRef = useRef(null);
  const isListeningRef = useRef(false);
  const wordContainerRef = useRef(null);
  const lastProcessedFinalRef = useRef('');

  const studentId = localStorage.getItem('schoolId');
  const studentName = localStorage.getItem('studentName') || localStorage.getItem('userName');
  const studentEmail = localStorage.getItem('userEmail') || '';
  const studentForm = localStorage.getItem('studentForm') || '';

  const getYearFromForm = (form) => {
    const match = form?.match(/^(\d+)/);
    return match ? `Year ${match[1]}` : form || '';
  };

  // Parse passage into words
  const passageWords = useMemo(() => {
    if (!assignment?.passage) return [];
    return assignment.passage
      .replace(/[^\w\s'-]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 0);
  }, [assignment]);

  // Normalize word for comparison
  const normalizeWord = useCallback((word) => {
    return (word || '').toLowerCase().replace(/[^a-z0-9'-]/g, '').trim();
  }, []);

  // Reading progress percentage
  const readingProgress = useMemo(() => {
    if (passageWords.length === 0) return 0;
    return Math.round((currentWordIndex / passageWords.length) * 100);
  }, [currentWordIndex, passageWords.length]);

  // Accuracy percentage
  const accuracyPercentage = useMemo(() => {
    if (currentWordIndex === 0) return 100;
    const correct = currentWordIndex - mistakes.length;
    return Math.round((correct / currentWordIndex) * 100);
  }, [currentWordIndex, mistakes.length]);

  // =====================
  // Data Loading
  // =====================
  useEffect(() => {
    const loadAssignment = async () => {
      try {
        const response = await fetch(buildApiUrl(`/api/reading/assignments/${id}/details`));
        const data = await response.json();
        if (data.success) {
          setAssignment(data.assignment);
        } else {
          setError('Assignment not found');
        }
      } catch (err) {
        setError('Failed to load assignment');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadAssignment();
  }, [id]);

  useEffect(() => {
    if (!studentId) return;
    const loadProgress = async () => {
      try {
        const response = await fetch(buildApiUrl(`/api/reading/progress/${id}/${studentId}`));
        const data = await response.json();
        if (data.success && data.progress) {
          setTimeSpent(data.progress.timeSpentSeconds || 0);
          setIsCompleted(data.progress.isCompleted || false);
        }
      } catch (err) {
        console.error('Error loading progress:', err);
      }
    };
    const loadRecording = async () => {
      try {
        const response = await fetch(buildApiUrl(`/api/reading/recordings/${id}/${studentId}`));
        const data = await response.json();
        if (data.success && data.recording) setExistingRecording(data.recording);
      } catch (err) {
        console.error('Error loading recording:', err);
      }
    };
    const loadAccuracy = async () => {
      try {
        const response = await fetch(buildApiUrl(`/api/reading/accuracy/${id}/${studentId}`));
        const data = await response.json();
        if (data.success && data.accuracy) {
          setExistingAccuracy(data.accuracy);
          if (data.accuracy.wordsRead > 0 && passageWords.length > 0) {
            const savedIndex = Math.min(data.accuracy.wordsRead, passageWords.length);
            setCurrentWordIndex(savedIndex);
            currentWordIndexRef.current = savedIndex;
            if (data.accuracy.mistakes && Array.isArray(data.accuracy.mistakes)) {
              setMistakes(data.accuracy.mistakes);
              mistakesRef.current = data.accuracy.mistakes;
            }
            const statuses = passageWords.map((_, i) => {
              if (i < savedIndex) {
                const wasMistake = (data.accuracy.mistakes || []).some(m => m.index === i);
                return wasMistake ? 'mistake' : 'correct';
              }
              return 'unread';
            });
            setWordStatuses(statuses);
            wordStatusesRef.current = statuses;
          }
        }
      } catch (err) {
        console.error('Error loading accuracy:', err);
      }
    };
    loadProgress();
    loadRecording();
    loadAccuracy();
  }, [id, studentId, passageWords]);

  // Init word statuses
  useEffect(() => {
    if (passageWords.length > 0 && wordStatuses.length === 0) {
      const statuses = passageWords.map(() => 'unread');
      setWordStatuses(statuses);
      wordStatusesRef.current = statuses;
      passageWordsRef.current = passageWords;
    }
  }, [passageWords, wordStatuses.length]);

  // Timer
  useEffect(() => {
    timerRef.current = setInterval(() => { setTimeSpent(prev => prev + 1); }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Save progress periodically
  const saveProgress = useCallback(async (completed = false, progressPct = null) => {
    if (!studentId || saving) return;
    setSaving(true);
    try {
      await fetch(buildApiUrl(`/api/reading/progress/${id}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId, studentName, studentEmail, studentForm,
          studentYear: getYearFromForm(studentForm),
          timeSpentSeconds: 30,
          isCompleted: completed || isCompleted,
          scrollPercentage: progressPct !== null ? progressPct : readingProgress
        })
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    } finally {
      setSaving(false);
    }
  }, [id, studentId, studentName, studentEmail, studentForm, readingProgress, isCompleted, saving]);

  useEffect(() => {
    saveIntervalRef.current = setInterval(() => { saveProgress(); }, 30000);
    return () => { if (saveIntervalRef.current) clearInterval(saveIntervalRef.current); };
  }, [saveProgress]);

  // Save accuracy
  const saveAccuracy = useCallback(async () => {
    if (!studentId || savingAccuracy || currentWordIndexRef.current === 0) return;
    setSavingAccuracy(true);
    const elapsed = readingStartTimeRef.current ? (Date.now() - readingStartTimeRef.current) / 1000 : 0;
    const wpm = elapsed > 0 ? Math.round((currentWordIndexRef.current / elapsed) * 60) : 0;
    try {
      await fetch(buildApiUrl(`/api/reading/accuracy/${id}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId, studentName, studentEmail, studentForm,
          studentYear: getYearFromForm(studentForm),
          totalWords: passageWordsRef.current.length,
          wordsRead: currentWordIndexRef.current,
          correctWords: currentWordIndexRef.current - mistakesRef.current.length,
          mistakeCount: mistakesRef.current.length,
          accuracyPercentage: currentWordIndexRef.current > 0
            ? Math.round(((currentWordIndexRef.current - mistakesRef.current.length) / currentWordIndexRef.current) * 100) : 100,
          wordsPerMinute: wpm,
          readingDurationSeconds: elapsed,
          mistakes: mistakesRef.current,
          progressPercentage: Math.round((currentWordIndexRef.current / passageWordsRef.current.length) * 100)
        })
      });
    } catch (err) {
      console.error('Error saving accuracy:', err);
    } finally {
      setSavingAccuracy(false);
    }
  }, [id, studentId, studentName, studentEmail, studentForm, savingAccuracy]);

  // Levenshtein distance
  const levenshtein = (a, b) => {
    const m = [];
    for (let i = 0; i <= b.length; i++) m[i] = [i];
    for (let j = 0; j <= a.length; j++) m[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        m[i][j] = b[i-1] === a[j-1]
          ? m[i-1][j-1]
          : Math.min(m[i-1][j-1]+1, m[i][j-1]+1, m[i-1][j]+1);
      }
    }
    return m[b.length][a.length];
  };

  const isSimilar = (a, b) => {
    if (a === b) return true;
    if (a.length < 2 || b.length < 2) return a === b;
    // Allow only 1 character difference for short words, max 2 for long words
    const maxLen = Math.max(a.length, b.length);
    const maxDist = maxLen <= 4 ? 1 : 2;
    return levenshtein(a, b) <= maxDist;
  };

  // Check if spoken word matches expected word
  const isWordMatch = (spoken, expected) => {
    if (spoken === expected) return true;
    if (isSimilar(spoken, expected)) return true;
    // Only exact homophones / common speech recognition substitutions
    const subs = {
      'the': 'da', 'to': 'too', 'to': 'two', 'two': 'too',
      'there': 'their', 'there': 'they\'re', 'theyre': 'their',
      'its': "it's", 'youre': "you're", 'were': "we're",
      'wont': "won't", 'dont': "don't", 'cant': "can't",
      'im': "i'm", 'ive': "i've", 'id': "i'd",
      'hes': "he's", 'shes': "she's", 'thats': "that's",
      'whats': "what's", 'lets': "let's", 'whos': "who's",
      'four': 'for', 'no': 'know', 'knew': 'new',
      'write': 'right', 'sea': 'see', 'hear': 'here',
      'wood': 'would', 'one': 'won', 'son': 'sun',
      'wear': 'where', 'piece': 'peace', 'through': 'threw'
    };
    if (subs[spoken] === expected || subs[expected] === spoken) return true;
    return false;
  };

  // Process interim results — advances pointer in real-time for tracking
  const processInterimText = useCallback((spokenText) => {
    const spokenWords = spokenText.toLowerCase().replace(/[^a-z0-9'\s-]/g, '').split(/\s+/).filter(w => w.length > 0);
    if (spokenWords.length === 0) return;

    const words = passageWordsRef.current;
    // Re-process from phrase start since interim text contains the full growing phrase
    let idx = phraseStartIdxRef.current;
    const statuses = [...wordStatusesRef.current];
    let advancedPast = currentWordIndexRef.current;

    for (let si = 0; si < spokenWords.length; si++) {
      if (idx >= words.length) break;

      // If this position was already confirmed by a previous final, skip
      if (idx < phraseStartIdxRef.current) { idx++; continue; }

      const expected = normalizeWord(words[idx]);
      const spokenNorm = normalizeWord(spokenWords[si]);

      if (isWordMatch(spokenNorm, expected)) {
        statuses[idx] = 'reading';
        idx++;
      } else {
        // In interim, don't mark mistakes — just skip this spoken word
        // (it could be noise, or the engine will correct it in final)
        continue;
      }
    }

    // Only advance if we went further than before
    if (idx > advancedPast) {
      currentWordIndexRef.current = idx;
      wordStatusesRef.current = statuses;
      setCurrentWordIndex(idx);
      setWordStatuses([...statuses]);
    } else {
      // Still update visual statuses (some words might have been marked 'reading')
      setWordStatuses([...statuses]);
    }

    interimWordIndexRef.current = idx;
    setInterimWordIndex(idx);

    // Auto-scroll to where we think the student is
    const scrollIdx = Math.min(Math.max(idx, advancedPast), words.length - 1);
    const el = document.getElementById(`word-${scrollIdx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Update WPM
    if (readingStartTimeRef.current && idx > 0) {
      const elapsed = (Date.now() - readingStartTimeRef.current) / 1000;
      if (elapsed > 0) setWordsPerMinute(Math.round((idx / elapsed) * 60));
    }
  }, [normalizeWord]);

  // Process final spoken words — goes back to phrase start, confirms correct and detects mistakes
  const processSpokenText = useCallback((spokenText, allAlternatives) => {
    const spokenWords = spokenText.toLowerCase().replace(/[^a-z0-9'\s-]/g, '').split(/\s+/).filter(w => w.length > 0);
    if (spokenWords.length === 0) return;

    // Build alternatives arrays for each position
    const altWords = (allAlternatives || []).map(alt =>
      alt.toLowerCase().replace(/[^a-z0-9'\s-]/g, '').split(/\s+/).filter(w => w.length > 0)
    );

    const words = passageWordsRef.current;
    // Go back to where this phrase started to properly detect mistakes
    let idx = phraseStartIdxRef.current;
    const statuses = [...wordStatusesRef.current];
    const curMistakes = [...mistakesRef.current];

    // Clear any 'reading' statuses from interim processing back to 'unread'
    for (let i = idx; i < statuses.length; i++) {
      if (statuses[i] === 'reading') statuses[i] = 'unread';
    }

    for (let wi = 0; wi < spokenWords.length; wi++) {
      if (idx >= words.length) break;
      const expected = normalizeWord(words[idx]);
      const spokenNorm = normalizeWord(spokenWords[wi]);

      // Check main transcript + all alternatives at this word position
      let matched = isWordMatch(spokenNorm, expected);
      if (!matched) {
        for (const alt of altWords) {
          if (wi < alt.length && isWordMatch(normalizeWord(alt[wi]), expected)) {
            matched = true;
            break;
          }
        }
      }

      if (matched) {
        statuses[idx] = 'correct';
        idx++;
      } else {
        // Look ahead up to 5 words to find a match (prevents cascade)
        let foundAhead = -1;
        for (let look = 1; look <= 5 && idx + look < words.length; look++) {
          const ahead = normalizeWord(words[idx + look]);
          if (spokenNorm === ahead || isSimilar(spokenNorm, ahead)) {
            foundAhead = idx + look;
            break;
          }
        }

        if (foundAhead > 0) {
          for (let s = idx; s < foundAhead; s++) {
            statuses[s] = 'mistake';
            curMistakes.push({ index: s, expected: words[s], spoken: '(skipped)' });
          }
          statuses[foundAhead] = 'correct';
          idx = foundAhead + 1;
        } else {
          statuses[idx] = 'mistake';
          curMistakes.push({ index: idx, expected: words[idx], spoken: spokenWords[wi] });
          idx++;
        }
      }
    }

    // Update phrase start for next speech phrase
    phraseStartIdxRef.current = idx;
    currentWordIndexRef.current = idx;
    wordStatusesRef.current = statuses;
    mistakesRef.current = curMistakes;
    interimWordIndexRef.current = -1;
    setInterimWordIndex(-1);
    setCurrentWordIndex(idx);
    setWordStatuses([...statuses]);
    setMistakes([...curMistakes]);

    // Auto-scroll
    const el = document.getElementById(`word-${idx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Update WPM
    if (readingStartTimeRef.current) {
      const elapsed = (Date.now() - readingStartTimeRef.current) / 1000;
      if (elapsed > 0) setWordsPerMinute(Math.round((idx / elapsed) * 60));
    }

    // Finished?
    if (idx >= words.length) {
      stopReadingSession();
      saveAccuracy();
    }
  }, [normalizeWord, saveAccuracy]);

  // =====================
  // 🧠 Speech Recognition
  // =====================
  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
      alert('Speech recognition is not supported in this browser. Please use Chrome.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-GB';
    recognition.maxAlternatives = 3;
    recognitionRef.current = recognition;
    isListeningRef.current = true;

    if (!readingStartTimeRef.current) {
      readingStartTimeRef.current = Date.now();
      setReadingStartTime(Date.now());
    }

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      let allAlternatives = [];
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + ' ';
          // Collect all alternatives for better matching
          for (let a = 1; a < result.length; a++) {
            allAlternatives.push(result[a].transcript);
          }
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      if (finalTranscript.trim()) {
        // Avoid reprocessing the same final transcript
        if (finalTranscript.trim() !== lastProcessedFinalRef.current) {
          lastProcessedFinalRef.current = finalTranscript.trim();
          setLastSpokenText(finalTranscript.trim());
          processSpokenText(finalTranscript.trim(), allAlternatives);
        }
      } else if (interimTranscript.trim()) {
        setLastSpokenText(interimTranscript.trim());
        // Process interim results for immediate visual feedback
        processInterimText(interimTranscript.trim());
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech error:', event.error);
      if (event.error === 'no-speech' || event.error === 'audio-capture' || event.error === 'network') {
        try { recognition.stop(); } catch (e) {}
        setTimeout(() => {
          if (isListeningRef.current) {
            try { recognition.start(); } catch (e) {}
          }
        }, 500);
      }
    };

    recognition.onend = () => {
      if (isListeningRef.current) {
        try { recognition.start(); } catch (e) {}
      }
    };

    try {
      recognition.start();
      setIsListening(true);
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
    }
  }, [processSpokenText, processInterimText]);

  const stopListening = useCallback(() => {
    isListeningRef.current = false;
    setIsListening(false);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current = null;
    }
    saveAccuracy();
  }, [saveAccuracy]);

  // Start combined session
  const startReadingSession = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      setAudioBlob(null);
      setAudioUrl(null);
      setUploadSuccess(false);

      recordingTimerRef.current = setInterval(() => { setRecordingTime(prev => prev + 1); }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
      alert('Please allow microphone access to record your reading.');
      return;
    }
    startListening();
  };

  const stopReadingSession = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
    stopListening();
  }, [isRecording, stopListening]);

  const handleMarkComplete = async () => {
    setIsCompleted(true);
    await saveProgress(true, readingProgress);
    await saveAccuracy();
    alert('Great job! You have completed this reading assignment.');
  };

  const discardRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setUploadSuccess(false);
  };

  const resetReading = () => {
    const statuses = passageWordsRef.current.map(() => 'unread');
    setCurrentWordIndex(0);
    currentWordIndexRef.current = 0;
    setInterimWordIndex(-1);
    interimWordIndexRef.current = -1;
    setWordStatuses(statuses);
    wordStatusesRef.current = statuses;
    setMistakes([]);
    mistakesRef.current = [];
    setWordsPerMinute(0);
    setReadingStartTime(null);
    readingStartTimeRef.current = null;
    setLastSpokenText('');
    lastProcessedFinalRef.current = '';
    phraseStartIdxRef.current = 0;
  };

  const uploadRecording = async () => {
    if (!audioBlob || !studentId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, `reading_${id}_${studentId}.webm`);
      formData.append('studentId', studentId);
      formData.append('studentName', studentName || '');
      formData.append('studentEmail', studentEmail);
      formData.append('studentForm', studentForm);
      formData.append('studentYear', getYearFromForm(studentForm));
      formData.append('durationSeconds', recordingTime.toString());

      const response = await fetch(buildApiUrl(`/api/reading/recordings/${id}/upload`), { method: 'POST', body: formData });
      const data = await response.json();
      if (data.success) {
        setUploadSuccess(true);
        setExistingRecording(data.recording);
      } else {
        alert('Failed to upload recording.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Error uploading recording.');
    } finally {
      setUploading(false);
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) {} }
    };
  }, [audioUrl]);

  const handleGoBack = async () => {
    await saveProgress(false, readingProgress);
    await saveAccuracy();
    navigate(-1);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}h ${remainMins}m`;
    }
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  };

  const getWordStyle = (status, isCurrent) => {
    if (isCurrent) return 'bg-yellow-200 text-gray-900 font-bold px-1 rounded';
    switch (status) {
      case 'correct': return 'text-green-600';
      case 'reading': return 'bg-blue-100 text-blue-700 px-0.5 rounded';
      case 'mistake': return 'bg-red-100 text-red-600 line-through px-0.5 rounded';
      default: return 'text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading reading assignment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <button onClick={handleGoBack} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold">{assignment?.title}</h1>
                <p className="text-yellow-100 text-sm">Reading Assignment</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="bg-white/20 rounded-lg px-3 py-1.5 text-sm">⏱️ {formatTime(timeSpent)}</div>
              <div className="bg-white/20 rounded-lg px-3 py-1.5 text-sm">📖 {readingProgress}%</div>
              <div className="bg-white/20 rounded-lg px-3 py-1.5 text-sm">🎯 {accuracyPercentage}%</div>
              {wordsPerMinute > 0 && (
                <div className="bg-white/20 rounded-lg px-3 py-1.5 text-sm">⚡ {wordsPerMinute} wpm</div>
              )}
              {isCompleted ? (
                <div className="bg-green-500 rounded-lg px-3 py-1.5 text-sm font-medium">✅ Completed</div>
              ) : (
                <button onClick={handleMarkComplete} className="bg-white text-orange-600 rounded-lg px-4 py-1.5 text-sm font-semibold hover:bg-orange-50 transition-colors">
                  Mark as Read ✓
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1.5 bg-orange-100">
        <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-300" style={{ width: `${readingProgress}%` }}></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{currentWordIndex}</div>
            <div className="text-xs text-gray-500">Words Read</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{passageWords.length}</div>
            <div className="text-xs text-gray-500">Total Words</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{currentWordIndex - mistakes.length}</div>
            <div className="text-xs text-gray-500">Correct</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{mistakes.length}</div>
            <div className="text-xs text-gray-500">Mistakes</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{wordsPerMinute}</div>
            <div className="text-xs text-gray-500">Words/Min</div>
          </div>
        </div>

        {/* Reading Passage with word highlighting */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 px-8 py-4 border-b border-orange-100 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{assignment?.title}</h2>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-xs text-green-600">
                <span className="w-3 h-3 rounded-full bg-green-200 inline-block"></span> Correct
              </span>
              <span className="flex items-center gap-1 text-xs text-red-600">
                <span className="w-3 h-3 rounded-full bg-red-200 inline-block"></span> Mistake
              </span>
              <span className="flex items-center gap-1 text-xs text-blue-700">
                <span className="w-3 h-3 rounded-full bg-blue-100 inline-block"></span> Reading
              </span>
              <span className="flex items-center gap-1 text-xs text-yellow-700">
                <span className="w-3 h-3 rounded-full bg-yellow-200 inline-block"></span> Current
              </span>
            </div>
          </div>

          <div ref={wordContainerRef} className="px-8 py-6 max-h-[50vh] overflow-y-auto">
            <div className="leading-10 text-lg">
              {passageWords.map((word, idx) => (
                <span
                  key={idx}
                  id={`word-${idx}`}
                  className={`inline-block mr-1.5 transition-colors duration-200 ${getWordStyle(wordStatuses[idx], idx === currentWordIndex)}`}
                >
                  {word}
                </span>
              ))}
            </div>
          </div>

          {/* Live speech feedback */}
          {isListening && lastSpokenText && (
            <div className="px-8 py-3 bg-blue-50 border-t border-blue-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-blue-700 font-medium">Heard:</span>
                <span className="text-sm text-blue-600 italic">"{lastSpokenText}"</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-gray-500">
              <span className="mr-4">📖 Read: <strong>{readingProgress}%</strong></span>
              <span className="mr-4">🎯 Accuracy: <strong>{accuracyPercentage}%</strong></span>
              <span>❌ Mistakes: <strong>{mistakes.length}</strong></span>
            </div>
            {!isCompleted && readingProgress >= 90 && (
              <button onClick={handleMarkComplete} className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg">
                ✅ I've finished reading
              </button>
            )}
          </div>
        </div>

        {/* 🎙️ Recording + Speech Control */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-red-50 to-pink-50 px-8 py-5 border-b border-red-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center text-white text-lg">🎙️</div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Read Aloud</h3>
                <p className="text-sm text-gray-500">Press Start, then read the passage aloud — AI will track your progress and mistakes in real time</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-6">
            {/* Existing Recording */}
            {existingRecording && !audioBlob && !isRecording && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">✅</span>
                    <div>
                      <p className="font-semibold text-green-800">Previous recording submitted</p>
                      <p className="text-sm text-green-600">
                        Duration: {formatTime(Math.round(existingRecording.durationSeconds || 0))}
                        {existingRecording.submittedAt && <span className="ml-2">• {new Date(existingRecording.submittedAt).toLocaleDateString()}</span>}
                      </p>
                    </div>
                  </div>
                  <audio controls src={buildApiUrl(`/api/reading/recordings/${id}/${studentId}/audio`)} className="h-10" />
                </div>
              </div>
            )}

            {/* Existing accuracy */}
            {existingAccuracy && !isRecording && !isListening && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📊</span>
                  <div>
                    <p className="font-semibold text-blue-800">Previous Reading Result</p>
                    <p className="text-sm text-blue-600">
                      Progress: {Math.round(existingAccuracy.progressPercentage)}%
                      • Accuracy: {Math.round(existingAccuracy.accuracyPercentage)}%
                      • {existingAccuracy.wordsRead}/{existingAccuracy.totalWords} words
                      • {existingAccuracy.mistakeCount} mistakes
                      • {Math.round(existingAccuracy.wordsPerMinute)} wpm
                    </p>
                  </div>
                </div>
              </div>
            )}

            {uploadSuccess && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                <span className="text-2xl">🎉</span>
                <div>
                  <p className="font-semibold text-green-800">Recording uploaded successfully!</p>
                  <p className="text-sm text-green-600">Your teacher will be able to listen to your reading.</p>
                </div>
              </div>
            )}

            {/* Recording visualizer */}
            <div className={`w-full h-24 rounded-xl flex items-center justify-center mb-6 ${
              isRecording ? 'bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300'
                : audioBlob ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200'
                : 'bg-gray-50 border-2 border-dashed border-gray-200'
            }`}>
              {isRecording ? (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-1 bg-red-500 rounded-full animate-pulse" style={{ height: `${20 + Math.random() * 30}px`, animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                  <div className="text-center">
                    <div className="text-red-600 font-bold text-xl">{formatTime(recordingTime)}</div>
                    <div className="text-red-400 text-xs uppercase tracking-wider">Recording & Listening...</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-1 bg-red-500 rounded-full animate-pulse" style={{ height: `${20 + Math.random() * 30}px`, animationDelay: `${i * 0.15 + 0.5}s` }} />
                    ))}
                  </div>
                </div>
              ) : audioBlob ? (
                <div className="flex flex-col items-center gap-2">
                  <audio ref={audioPlayerRef} src={audioUrl} controls onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onEnded={() => setIsPlaying(false)} className="h-10" />
                  <p className="text-sm text-gray-500">Duration: {formatTime(recordingTime)}</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-400 text-lg">🎤</p>
                  <p className="text-gray-400 text-sm">Click Start to begin reading aloud</p>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex items-center justify-center gap-4 flex-wrap">
              {!isRecording && !audioBlob && (
                <>
                  <button onClick={startReadingSession}
                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                    </svg>
                    {existingRecording ? 'Start Again' : 'Start Reading'}
                  </button>
                  {currentWordIndex > 0 && (
                    <button onClick={resetReading} className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 transition-all">
                      🔄 Reset Progress
                    </button>
                  )}
                </>
              )}

              {isRecording && (
                <button onClick={stopReadingSession}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-gray-700 to-gray-900 text-white rounded-xl font-semibold hover:from-gray-800 hover:to-black transition-all shadow-lg animate-pulse">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2"/>
                  </svg>
                  Stop Reading
                </button>
              )}

              {audioBlob && !isRecording && (
                <>
                  <button onClick={discardRecording} className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 transition-all">
                    🗑️ Discard
                  </button>
                  <button onClick={() => { discardRecording(); resetReading(); }} className="flex items-center gap-2 px-6 py-3 border-2 border-orange-300 text-orange-600 rounded-xl font-semibold hover:bg-orange-50 transition-all">
                    🔄 Re-record
                  </button>
                  <button onClick={uploadRecording} disabled={uploading}
                    className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all shadow-lg ${
                      uploading ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 hover:shadow-xl transform hover:scale-105'
                    }`}>
                    {uploading ? (
                      <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading...</>
                    ) : (
                      <>📤 Submit Recording</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mistakes List */}
        {mistakes.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 px-8 py-4 border-b border-red-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xl">❌</span>
                <h3 className="text-lg font-bold text-gray-900">Mistakes ({mistakes.length})</h3>
              </div>
              <span className="text-sm text-gray-500">🎯 Accuracy: {accuracyPercentage}%</span>
            </div>
            <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
              {mistakes.map((m, idx) => (
                <div key={idx} className="px-8 py-3 flex items-center gap-4 hover:bg-red-50/50">
                  <span className="text-xs text-gray-400 w-8">#{idx + 1}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">{m.expected}</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-sm font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded">{m.spoken}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingAssignmentViewer;
